// Notification-preferences section on the Profile page.
// Lets the user toggle which event kinds trigger in-app + web-push notifications.
import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Bell, ShoppingBag, Wallet, Megaphone, MessageCircle } from "lucide-react";

const LOCAL_KEY = "talabya_notif_prefs_v1";

const DEFAULTS = { order_updates: true, wallet_updates: true, promos: true, chat: true };

const ITEMS = [
  { key: "order_updates", Icon: ShoppingBag,   label: "تحديثات الطلبات",  desc: "حالة الطلب، السائق في الطريق، التسليم" },
  { key: "wallet_updates", Icon: Wallet,       label: "المحفظة والمعاملات", desc: "شحن، خصم، استرداد، استبدال نقاط" },
  { key: "chat",           Icon: MessageCircle, label: "رسائل السائق",      desc: "تلقّي رسائل من سائقك أثناء التوصيل" },
  { key: "promos",         Icon: Megaphone,    label: "العروض والتخفيضات",  desc: "عروض المطاعم والحملات الترويجية" },
];

export default function NotificationPreferences({ user, onUpdated }) {
  const remote = useMemo(() => ({ ...DEFAULTS, ...(user?.notification_prefs || {}) }), [user?.notification_prefs]);
  const [prefs, setPrefs] = useState(remote);
  const [busyKey, setBusyKey] = useState(null);

  // Sync local state whenever the remote prefs actually change (stable identity via useMemo).
  useEffect(() => {
    setPrefs(remote);
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(remote)); } catch { /* ignore */ }
  }, [remote]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
    setBusyKey(key);
    try {
      await api.patch("/auth/me/notifications", { [key]: next[key] });
      onUpdated?.();
    } catch {
      setPrefs(prefs);
      localStorage.setItem(LOCAL_KEY, JSON.stringify(prefs));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="bg-white rounded-3xl talabya-shadow-soft p-5" data-testid="notif-prefs-section">
      <div className="flex items-center gap-2 mb-1">
        <Bell size={18} className="text-primary-brand"/>
        <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>تفضيلات الإشعارات</h2>
      </div>
      <p className="text-xs text-[var(--text-secondary)] mb-4">اختر نوع الإشعارات التي تود استقبالها.</p>
      <ul className="space-y-2">
        {ITEMS.map(({ key, Icon, label, desc }) => {
          const active = !!prefs[key];
          const busy = busyKey === key;
          return (
            <li
              key={key}
              className={`flex items-center gap-3 p-3 rounded-2xl transition-colors ${active ? "bg-blue-50 border-2 border-primary-brand/30" : "bg-[var(--surface-alt)] border-2 border-transparent"}`}
              data-testid={`notif-pref-${key}`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)]"}`}>
                <Icon size={16}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm">{label}</div>
                <div className="text-[11px] text-[var(--text-secondary)] leading-relaxed">{desc}</div>
              </div>
              <button
                onClick={() => toggle(key)}
                disabled={busy}
                className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${active ? "bg-primary-brand" : "bg-gray-300"} disabled:opacity-60`}
                aria-label={`toggle ${key}`}
                data-testid={`notif-pref-toggle-${key}`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${active ? "right-1" : "right-6"}`}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function readCachedPrefs() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
